import { useEffect, useState } from "react";
import { Search } from "lucide-react";

export type StatusTab = "all" | "published" | "scheduled" | "drafts";

export interface ToolbarFilters {
  search: string;
  status: StatusTab;
}

const STATUS_TABS: ReadonlyArray<{ value: StatusTab; label: string }> = [
  { value: "all", label: "All" },
  { value: "published", label: "Published" },
  { value: "scheduled", label: "Scheduled" },
  { value: "drafts", label: "Drafts" },
];

interface Props {
  filters: ToolbarFilters;
  onFiltersChange: (next: ToolbarFilters) => void;
}

/**
 * Sits at the top of the Blog list card. Search box on the left,
 * status segmented control on the right — same arrangement as the
 * mock, plus a Scheduled tab the design didn't include but the data
 * model supports.
 */
export function BlogListToolbar({ filters, onFiltersChange }: Props) {
  // Debounced search keeps the parent quiet while the user types.
  const [searchDraft, setSearchDraft] = useState(filters.search);

  useEffect(() => {
    if (searchDraft === filters.search) return;
    const id = window.setTimeout(() => {
      onFiltersChange({ ...filters, search: searchDraft });
    }, 200);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  useEffect(() => {
    if (filters.search !== searchDraft) setSearchDraft(filters.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  return (
    <div className="flex items-center gap-2">
      <div className="relative">
        <Search
          size={12}
          strokeWidth={2}
          className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-5"
        />
        <input
          type="text"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          placeholder="Search posts…"
          aria-label="Search posts"
          className="hair w-48 rounded-md border bg-paper py-1.5 pl-8 pr-3 text-[12.5px] placeholder:text-ink-5 focus:border-tide focus:outline-none"
        />
      </div>
      <div className="hair flex items-center gap-1 rounded-md border bg-paper p-0.5">
        {STATUS_TABS.map((tab) => {
          const active = filters.status === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              onClick={() => onFiltersChange({ ...filters, status: tab.value })}
              className={`rounded px-2.5 py-1 text-[11.5px] transition ${
                active
                  ? "shadow-card bg-paper-hi font-medium text-ink"
                  : "text-ink-3 hover:bg-paper-2"
              }`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
